import { z } from "zod"

export const PROFESSIONS = [
	{
		id: "delivery",
		l10n: {
			title: "Delivery, Technical Services and Maintenance",
			description:
				"Traveling between multiple locations for deliveries, repairs, installations, or service tasks",
			commonCharacteristics:
				"Frequent short trips between different addresses (offices, homes, warehouses).",
			workingHours: {
				primary: "Primarily weekdays",
				details:
					"(Mon–Fri, often 8:00–17:00 or 9:00–18:00), but can include emergency calls.",
			},
			mainPurpose: "installation, repair, delivery, or equipment servicing.",
			professions: [
				"Handyman",
				"Field Service Engineer/Technician (On-Site)",
				"On-Site IT Support Specialist",
				"Delivery & Installation Manager (Software/Hardware)",
				"Traveling Cleaning Specialist",
				"Driver/Courier (using personal car)",
				"Car Rental Agent (Pickup/Delivery)",
				"and other",
			],
		},
		timeSpent: {
			timeAtLocation: {
				from: 0.5 * 60,
				to: 1 * 60,
			},
			timeInOffice: {
				from: 1 * 60,
				to: 3 * 60,
			},
			tripsPerDay: {
				from: 3,
				to: 10,
			},
		},
		radiusMiles: 5,
		addressesPerYear: {
			min: 100,
			max: 300,
		},
	},
	{
		id: "sales",
		l10n: {
			title: "Sales, Marketing, and Real Estate",
			description:
				"Frequent visits to clients, presentations, negotiations, possible business trips",
			commonCharacteristics:
				"Regular travel to client offices, property sites, and neutral meeting venues for business development.",
			workingHours: {
				primary: "Primarily weekdays",
				details:
					"(Mon–Fri, 9:00–18:00), with occasional weekend meetings for client availability.",
			},
			mainPurpose:
				"client meetings, property showings, presentations, and negotiations.",
			professions: [
				"Real Estate Agent",
				"Sales Representative",
				"Business Development Manager",
				"Marketing Consultant",
				"Property Manager",
				"Account Executive",
				"Insurance Agent",
				"and other",
			],
		},
		timeSpent: {
			timeAtLocation: {
				from: 0.5 * 60,
				to: 2 * 60,
			},
			timeInOffice: {
				from: 2 * 60,
				to: 4 * 60,
			},
			tripsPerDay: {
				from: 2,
				to: 6,
			},
		},
		radiusMiles: 7,
		addressesPerYear: {
			min: 80,
			max: 300,
		},
	},
	{
		id: "finance",
		l10n: {
			title: "Finance, Law, and Consulting",
			description:
				"Business meetings at client offices, visiting government agencies or courts, negotiations",
			commonCharacteristics:
				"Regular visits to client offices, government agencies, and banks within the metro area.",
			workingHours: {
				primary: "Standard business hours",
				details:
					"(Mon–Fri, 9:00–18:00), with occasional extra hours for urgent matters.",
			},
			mainPurpose:
				"consultations, legal proceedings, financial meetings, and client advisory sessions.",
			professions: [
				"Financial Advisor",
				"Business Consultant",
				"Attorney/Lawyer",
				"Tax Consultant",
				"Investment Advisor",
				"Accountant (On-Site)",
				"Legal Consultant",
				"and other",
			],
		},
		timeSpent: {
			timeAtLocation: {
				from: 1 * 60,
				to: 3 * 60,
			},
			timeInOffice: {
				from: 4 * 60,
				to: 6 * 60,
			},
			tripsPerDay: {
				from: 1,
				to: 3,
			},
		},
		radiusMiles: 5,
		addressesPerYear: {
			min: 30,
			max: 150,
		},
	},
	{
		id: "healthcare",
		l10n: {
			title: "Healthcare, Social Services, and Personal Care",
			description:
				"Visiting patients and medical facilities, making home visits, and coordinating with health agencies",
			commonCharacteristics:
				"Regular visits to patients' homes, clinics, social centers, and healthcare facilities within local area.",
			workingHours: {
				primary: "Flexible schedule",
				details:
					"(Some Mon-Fri 9:00-17:00, others include evenings/weekends) based on patient needs.",
			},
			mainPurpose:
				"patient care, therapy sessions, treatments, and personal care services.",
			professions: [
				"Home Health Nurse",
				"Physical Therapist",
				"Social Worker",
				"Personal Care Assistant",
				"Occupational Therapist",
				"Mental Health Counselor",
				"Mobile Medical Provider",
				"and other",
			],
		},
		timeSpent: {
			timeAtLocation: {
				from: 1 * 60,
				to: 2 * 60,
			},
			timeInOffice: {
				from: 1 * 60,
				to: 4 * 60,
			},
			tripsPerDay: {
				from: 2,
				to: 6,
			},
		},
		radiusMiles: 5,
		addressesPerYear: {
			min: 50,
			max: 200,
		},
	},
	{
		id: "creative",
		l10n: {
			title: "Creative, Event Services, Education, and Miscellaneous",
			description: "Providing services at clients' homes or private settings",
			commonCharacteristics:
				"Variable locations including studios, event venues, private homes, and schools.",
			workingHours: {
				primary: "Highly flexible schedule",
				details:
					"Including evenings and weekends for events, shoots, and lessons.",
			},
			mainPurpose:
				"creative sessions, events, performances, and educational services.",
			professions: [
				"Photographer",
				"Event Planner",
				"Private Tutor",
				"Music Teacher",
				"Personal Trainer",
				"Wedding Coordinator",
				"Art Instructor",
				"and other",
			],
		},
		timeSpent: {
			timeAtLocation: {
				from: 1 * 60,
				to: 3 * 60,
			},
			timeInOffice: {
				from: 0,
				to: 2 * 60,
			},
			tripsPerDay: {
				from: 2,
				to: 6,
			},
		},
		radiusMiles: 10,
		addressesPerYear: {
			min: 50,
			max: 300,
		},
	},
] as const satisfies Profession[]

export const professionId = z.union([
	z.literal("delivery"),
	z.literal("sales"),
	z.literal("finance"),
	z.literal("healthcare"),
	z.literal("creative"),
])
export type ProfessionId = z.infer<typeof professionId>

export type Profession = {
	id: ProfessionId
	l10n: {
		title: string
		description: string
		commonCharacteristics: string
		workingHours: {
			primary: string
			details: string
		}
		mainPurpose: string
		professions: string[]
	}
	timeSpent: {
		tripsPerDay: {
			from: number
			to: number
		}
		timeAtLocation: {
			from: number
			to: number
		}
		timeInOffice: {
			from: number
			to: number
		}
	}
	radiusMiles: number
	addressesPerYear: {
		min: number
		max: number
	}
}
